// Small pure helpers for the Groups tab, extracted so column-render rules are
// unit-testable without standing up the full tab (query client + API mocks).
import type { DateWindowValue } from "@/components/ui/date-window-filter";

// IR-1384: the single "Added" control maps onto the two query shapes the
// groups-unified endpoint already accepts — a rolling window token, or an
// explicit range over the same entity date (system_creation_time). The control
// guarantees they are mutually exclusive; this only translates whichever is
// set, so a stale range can never ride along with a window.
export function addedFilterParams(value: DateWindowValue): {
  added_within?: string;
  added_after?: string;
  added_before?: string;
} {
  if (value.within) return { added_within: value.within };
  const params: { added_after?: string; added_before?: string } = {};
  if (value.range.from) params.added_after = value.range.from;
  if (value.range.to) params.added_before = value.range.to;
  return params;
}

// The Avatars column shows WHICH avatars cover the group, not just how many
// (Harel 2026-07-02: "we don't know which avatar is responsible for fetching
// those groups"). First `max` names inline, "+N" overflow, em-dash when the
// group has no avatar mapping. The full list stays on the cell tooltip and in
// the detail panel.
export function fmtAvatarNames(avatars: { avatar_name: string }[], max = 2): string {
  if (avatars.length === 0) return "—";
  const names = avatars.map((a) => a.avatar_name);
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max}`;
}

// IR-1147 reopen (Tomer 2026-06-30): the Avatars-column tooltip shows each
// connected avatar's mobile number under its device name. Not every avatar has
// a phone on file (backend phone_number is nullable / can be blank), so render
// an explicit placeholder rather than an empty line that reads as a bug.
export function fmtAvatarPhone(phone: string | null | undefined): string {
  const trimmed = phone?.trim();
  return trimmed ? trimmed : "No mobile number";
}

// ─── IR-1425 / IR-1426: the three header numbers + the "Added groups" fold ───

// A header figure, or a bare em-dash. Null and ABSENT both take the dash, for
// two different reasons that must look identical on screen:
//   absent — the summary block does not exist on today's prod payload, because
//            iris-ops deploys to prod without asking (the ops carve-out) while
//            iris-backend waits on an explicitly Harel-initiated prod pick
//   null   — the fleet DB was unreachable for `total_collected` specifically
// A 0 in either case would be a confident claim ("no group is on any phone")
// drawn from an answer we never received.
export function formatSummaryNumber(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : n.toLocaleString();
}

// ─── IR-1426 r2: the Reported filter (supersedes the r1 "Added groups" fold) ──
//
// Rotem, 2026-08-24: "add filter 'Reported' / 'Excel'".
//
// The r1 fold partitioned `on_phone === false` out of the CURRENT PAGE, which
// is all a client-side control can do against a server-paginated table. It read
// as a filter and was not one: its count described 50 rows while the tab
// claimed thousands. The replacement pushes the axis to the endpoint
// (`?on_phone=`), so the count, the page and `total` all describe one
// population. The fold is gone rather than kept alongside — two controls on one
// axis, one page-local and one fleet-wide, is a guaranteed disagreement.
//
// 🔑 THREE buckets, not Rotem's two. `on_phone` is a TRI-STATE and its third
// value is most of the fleet today: until IR-1433 is fixed no WhatsApp avatar
// reports at all, so those rows are null/absent. Folding them into "Excel"
// would accuse the whole WhatsApp estate of being stale assignments on the
// strength of an observation nobody has made. `never_reported` is its own,
// separately selectable bucket, and it is named for what it is.
// IR-1426 r3 (Rotem 2026-08-31: "Currently Reported / Stopped Reporting /
// Never Reported") — the labels are HER three words, the same ones the
// backend's COVERAGE_LABELS put on the Coverage column, so the filter and the
// column it filters cannot read as two vocabularies. The VALUES are the API
// contract (`?on_phone=`) and stay unchanged.
export const ON_PHONE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "reported", label: "Currently Reported" },
  { value: "not_reported", label: "Stopped Reporting" },
  { value: "never_reported", label: "Never Reported" },
];

// The capability handshake. iris-ops reaches prod ahead of iris-backend (the
// ops carve-out vs. an explicitly Harel-initiated prod pick), so the control is
// drawn ONLY when the payload says the endpoint honours the param. A filter
// rendered against a backend that ignores it returns the unfiltered page and
// looks exactly like a filter that matched everything.
export function supportsOnPhoneFilter(summary?: {
  supported_filters?: string[] | null;
}): boolean {
  return (summary?.supported_filters ?? []).includes("on_phone");
}

// ─── IR-1627: the "Messages Read" filter ────────────────────────────────────
//
// Rotem (c63023, Hebrew, translated): "I see the column — can you add a
// filter on the message-collection status so I can filter? Otherwise it's
// hard for me to check all the states."
//
// Same shape as the Reported filter above: SERVER-side (the table is
// paginated server-side, so a client-side partition could only ever narrow
// the page already fetched), gated behind the same capability handshake, and
// named with the matrix's own values (api/ops/services/messages_quality.py,
// `LABELS`) — no synonyms. Unknown is included and never folded into another
// bucket: it is most of the fleet today and the value she most needs to
// isolate to "check all the states" (Ingestion Matrix rule 4).
//
// IR-1627 (Rotem c63064, Hebrew, translated): "Messages Read: N.A for
// current collection after confirmed stop... Include N.A in the requested
// Messages Read filter, distinctly from Unknown." `not_applicable` is its
// own, fourth state here — never unioned into `unknown` on either side
// (Matrix rule 4: N.A is a positive answer, not an absence).
export const MESSAGES_QUALITY_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "complete", label: "Complete" },
  { value: "partial", label: "Partial" },
  { value: "unknown", label: "Unknown" },
  { value: "not_applicable", label: "Not Applicable" },
];

export function supportsMessagesQualityFilter(summary?: {
  supported_filters?: string[] | null;
}): boolean {
  return (summary?.supported_filters ?? []).includes("messages_quality");
}

// ─── IR-1482 r2: ring slice colours ──────────────────────────────────────────
//
// Rotem (IR-1228, 2026-09-07 23:01): "fix the graphs to be connected" and
// "Ingestion - I defined 4 states and I want them - check again".
//
// 🪤 WHY THESE EXIST AT ALL. r1 passed `colorFor={() => undefined}`, so
// `donutSlices` fell back to `chartColor(i)` — colour by INDEX. The backend
// sorts slice labels alphabetically, so the moment r2 seeds the empty ingestion
// states the list grows from 2 entries to 4 and every existing slice is
// repainted: "Ingested" would hand its colour to "Batch ingestion failed"
// purely because B sorts before I. Colour has to follow the state, not its
// position, or the ring silently re-labels itself between two deploys.
//
// Hex rather than `var()`: SVG fill attributes do not resolve custom properties
// reliably — the same reason `CONNECTED_COLLECTION_COLORS` on the Avatars tab
// is written out. Values ARE the iris tokens, listed beside each one.
//
// Checked with the dataviz palette validator (dark, surface --iris-bg-2
// #0b0d14, --pairs all): CVD separation and the normal-vision floor PASS on
// every pair in both rings. Two deviations are deliberate and shared with every
// other chart in this app: the neon ramp sits above the validator's lightness
// band (house palette — changing it here alone would make these two rings look
// like a different product), and the grey below its chroma floor is the
// "nothing happened here" neutral, which is a meaning, not a washed-out hue.
//
// IR-1726 r2: the backend (`resolve_group_ingestion`) now emits a ten-label
// vocabulary — five of them reachable inside the Connected ring today, zeros
// included (Messages Ingested, Group ingestion failed, Batch ingestion failed,
// Not Applicable, Ingested after stop); the other five can appear on a row.
// Every label has an entry so the ring never falls back to colour-by-index as
// the alphabetical legend grows. The set was re-validated with the dataviz
// palette validator in alphabetical legend order on the dark surface #0b0d14:
// CVD separation PASS, normal-vision floor PASS; the lightness/chroma
// deviations are the house-palette exemptions already noted above; one
// contrast WARN on the dim purple (#6a2bb0, "No usable payload") is relieved
// by the legend's visible labels + counts — never colour alone.
const INGESTION_SLICE_COLORS: Record<string, string> = {
  // Legacy label from a backend that predates r2 (prod before the port, the
  // Avatars-tab roll-up). Same green as "Messages Ingested" ON PURPOSE: the two
  // never co-occur in one ring, and they mean the same thing.
  Ingested: "#39ff88", // --iris-neon-green // token-exempt
  // Grey, not red: never ingested is an absence, not a failure.
  "Never ingested": "#8b93a7", // --iris-fg-3 // token-exempt
  "Group ingestion failed": "#ff3355", // --iris-neon-red // token-exempt
  "Batch ingestion failed": "#ff9933", // --iris-neon-orange // token-exempt
  // IR-1726 r2: the "data landed" family — messages, roster, identity.
  "Messages Ingested": "#39ff88", // --iris-neon-green // token-exempt
  "Roster Ingested": "#22d3ff", // --iris-neon-cyan // token-exempt
  "Identity Ingested": "#3e8dff", // --iris-neon-blue // token-exempt
  // The contradiction — items landed AFTER a stop decision — gets its own
  // hue, nothing near the greens or the neutral grey.
  "Ingested after stop": "#ff2ea6", // --iris-neon-magenta // token-exempt
  // Absences and settled states.
  "No usable payload": "#6a2bb0", // --iris-neon-purple-dim // token-exempt
  "Already imported historically": "#a855f7", // --iris-neon-purple // token-exempt
  // Same grey the Avatars ring uses for n/a.
  "Not Applicable": "#6b7184", // --iris-fg-4 // token-exempt
};

// IR-1425 r10: the Collection ring's colours now come from the served
// `by_collection_axis` items (`lcpSliceHexForItem` in `@/lib/lifecycle-matrix`),
// so the label-keyed `LIFECYCLE_SLICE_COLORS` table is gone — a relabel must
// not recolour anything (G14). The ingestion ring is unchanged.

export function ingestionSliceColor(label: string): string | undefined {
  return INGESTION_SLICE_COLORS[label];
}

// ─── IR-1697 r11 (QA finding 1): the Ingestion filter and rings PER ENVIRONMENT ───
//
// Ticket: "Make ingestion filters, sorting, statistics ... explicitly
// environment-specific, in both tabs." Rotem 64438: "The summary will be
// separate for PROD and for DEV."
//
// The tokens are exactly what iris-backend `group_ingestion_population.parse_filter`
// accepts (`FILTER_TOKENS` = `GROUP_INGESTION_STATES` + `could_not_check` +
// `unknown`) and the labels are its `L3_LABELS`, so a filter option, a ring slice
// and a row cell read the same word. The same list is offered for PROD and for DEV.

/**
 * The Ingestion minimum width: PROD state, DEV state and the receipt line at 1440.
 * IR-1697 r16: back to r14's 190 (230 pushed Last Monitoring under the sticky
 * Delete column); the instant itself stays nowrap (ReceiptTimeText).
 */
export const INGESTION_COL_MIN_WIDTH = 190;

export const ENV_INGESTION_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "messages_ingested", label: "Messages Ingested" },
  { value: "roster_ingested", label: "Roster Ingested" },
  { value: "identity_ingested", label: "Identity Ingested" },
  { value: "no_usable_payload", label: "No usable payload" },
  { value: "never_ingested", label: "Never ingested" },
  { value: "group_ingestion_failed", label: "Group ingestion failed" },
  { value: "batch_ingestion_failed", label: "Batch ingestion failed" },
  { value: "not_applicable", label: "Not Applicable" },
  { value: "already_imported_historically", label: "Already imported historically" },
  { value: "ingested_after_stop", label: "Ingested after stop" },
  { value: "could_not_check", label: "Could Not Check" },
  { value: "unknown", label: "Unknown" },
];

/**
 * Slice colour for the PROD / DEV rings. The ten group states keep the colours
 * the Groups ring already uses; Could Not Check is the cyan the Avatars PROD/DEV
 * rings use for it, and Unknown is its own grey (never the Not Applicable grey).
 */
export function envIngestionSliceColor(label: string): string | undefined {
  if (label === "Could Not Check") return "#22d3ff"; // --iris-neon-cyan // token-exempt
  if (label === "Unknown") return "#8b93a7"; // --iris-fg-3 // token-exempt
  return ingestionSliceColor(label);
}

/** One environment's ring items as the `{label, count}` rows `ConnectedDonut` takes. */
export function envIngestionRing(
  breakdown:
    | {
        by_ingestion_env?: {
          prod: { label: string; count: number }[];
          dev: { label: string; count: number }[];
        };
      }
    | undefined,
  env: "prod" | "dev",
): { label: string; count: number }[] {
  return (breakdown?.by_ingestion_env?.[env] ?? []).map((i) => ({
    label: i.label,
    count: i.count,
  }));
}
